<template>
  <div class="flex flex-col space-y-3">
    <label v-if="label" :for="internalId">
      <slot name="label">
        <span class="ui-text-sm" :class="white ? 'text-white' : 'text-primary'">
          {{ label }}
        </span>
      </slot>
    </label>
    <div
      class="flex items-center gap-0.5 border"
      :class="white ? 'border-black-40' : 'border-primary'"
    >
      <slot name="decrement">
        <UiButton
          variant="clear"
          :disabled="decrementDisabled || disabled"
          class="inline-flex w-6 h-8.5 shrink-0 items-center justify-center py-0 pr-0 pl-2 !border-0 !bg-transparent disabled:opacity-50"
          :class="white ? '!text-white' : '!text-primary'"
          :aria-label="$t('decrementValue')"
          type="button"
          @click="decrement"
        >
          <UiIcon name="minus" class="size-3.5" />
        </UiButton>
      </slot>
      <input
        :id="internalId"
        v-model.number="currentValue"
        type="number"
        :disabled="disabled"
        :min="min"
        :max="max"
        :step="step"
        :class="
          cn(
            'h-8.5 w-full grow bg-transparent ui-text-sm font-medium text-primary placeholder-black-60 focus:outline-none text-center',
            white ? '!text-white' : '!text-primary',
            inputClasses,
          )
        "
        @blur="updateValue(Number(($event.target as HTMLInputElement).value))"
        @keydown.esc="
          updateValue(Number(($event.target as HTMLInputElement).value))
        "
        @keydown.enter="
          updateValue(Number(($event.target as HTMLInputElement).value))
        "
        @keydown.up.prevent="increment"
        @keydown.down.prevent="decrement"
      />
      <UiButton
        variant="clear"
        :disabled="incrementDisabled || disabled"
        class="inline-flex w-6 h-8.5 shrink-0 items-center justify-center rounded-full py-0 pl-0 pr-2 !border-0 !bg-transparent disabled:opacity-50"
        :class="white ? '!text-white' : '!text-primary'"
        :aria-label="$t('incrementValue')"
        type="button"
        @click="increment"
      >
        <UiIcon name="plus" class="size-3.5" />
      </UiButton>
    </div>
    <SharedFieldHint
      v-if="!hideDetails"
      :error="Boolean(errorMessage)"
      :message="errorMessage || ''"
    />
  </div>
</template>

<script setup lang="ts">
import { useField } from "vee-validate";
const props = withDefaults(
  defineProps<{
    disabled?: boolean;
    max?: number | string;
    min?: number | string;
    modelValue: number | string;
    step?: number | string;
    precision?: number;
    inputClasses?: string | any[] | object;
    name?: string;
    lazy?: boolean;
    hideDetails?: boolean;
    label?: string;
    white?: boolean;
  }>(),
  {
    max: Infinity,
    min: -Infinity,
    step: 1,
    precision: 0,
    inputClasses: "",
    name: "",
    lazy: true,
    hideDetails: false,
    label: "",
    white: false,
  },
);

const emit = defineEmits(["update:modelValue"]);

const internalId = useId();
const name = toRef(props, "name");
const { errorMessage, handleChange, handleBlur, meta } = useField(
  name,
  undefined,
  {
    initialValue: props.modelValue,
  },
);

const currentValue = ref(Number(props.modelValue));

function myHandleBlur(e) {
  handleChange(e);
  handleBlur(e);
}

const decrementDisabled = ref(false);
const incrementDisabled = ref(false);

const minNumber = computed(() => {
  return Number(props.min);
});

const maxNumber = computed(() => {
  return Number(props.max);
});

const stepNumber = computed(() => {
  return Number(props.step);
});

watchEffect(() => {
  decrementDisabled.value = Number(currentValue.value) === minNumber.value;
  incrementDisabled.value = Number(currentValue.value) === maxNumber.value;
});

watch(
  () => props.modelValue,
  (val) => {
    currentValue.value = Number(val);
  },
);

function increment() {
  if (props.disabled || incrementDisabled.value) {
    return;
  }

  const newVal = currentValue.value + stepNumber.value;

  updateValue(Number(newVal.toFixed(props.precision)));
}
function decrement() {
  if (props.disabled || decrementDisabled.value) {
    return;
  }

  const newVal = currentValue.value + -1 * stepNumber.value;

  updateValue(Number(newVal.toFixed(props.precision)));
}
function updateValue(newVal: number) {
  if (newVal <= minNumber.value) {
    newVal = minNumber.value;
  }
  if (newVal >= maxNumber.value) {
    newVal = maxNumber.value;
  }
  currentValue.value = Number(newVal.toFixed(props.precision));
  if (props.modelValue !== currentValue.value) {
    emit("update:modelValue", currentValue.value);
    myHandleBlur(currentValue.value);
  }
}

defineExpose({
  meta,
  value: props.modelValue,
  name: props.name,
});
</script>
